import React, { useEffect, useState } from 'react'
import useOrgJwt from '../hooks/useOrgJwt'
import cfg from '../config/billing'
import './Billing.css'
import { formatDateRange } from '../utils/dateFormatter'
import EmptyState from '../components/EmptyState'

export default function Billing() {
  const { orgJwt, loading: jwtLoading } = useOrgJwt()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tokenMissing, setTokenMissing] = useState(false)
  const openPortal = async () => {
    try {
      if (!orgJwt) { setTokenMissing(true); return }
      const r = await fetch(`${cfg.apiBase}/billing/create-portal-session`, { method:'POST', headers: { 'Content-Type':'application/json', Authorization: `Bearer ${orgJwt}` }, body: JSON.stringify({ returnUrl: window.location.origin + '/billing' }) })
      if (!r.ok) throw new Error('portal_failed')
      const j = await r.json()
      window.location.href = j.url
    } catch { setError('Failed to open portal') }
  }
  useEffect(() => {
    if (jwtLoading) return
    if (!orgJwt) {
      setLoading(false)
      setTokenMissing(true)
      return
    }
    let cancelled = false
    const run = async () => {
      setLoading(true)
      setError('')
      setTokenMissing(false)
      try {
        const r = await fetch(`${cfg.apiBase}/billing/me`, { headers: { Authorization: `Bearer ${orgJwt}` } })
        if (!r.ok) throw new Error('load_failed')
        const j = await r.json()
        if (!cancelled) setData(j)
      } catch (e) {
        if (!cancelled) setError('Failed to load billing')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [jwtLoading, orgJwt])

  if (loading || jwtLoading) return (
    <div className="billing-page">
      <EmptyState
        icon="pi-spin pi-spinner"
        title="Loading billing information..."
        variant="subtle"
      />
    </div>
  )
  if (tokenMissing) return (
    <div className="billing-page">
      <EmptyState
        icon="pi-credit-card"
        title="No billing information"
        description="Join or create an organization to view billing details and manage your subscription."
        action={
          <a href="/pricing" className="btn btn-primary">View Plans</a>
        }
      />
    </div>
  )
  if (error) return (
    <div className="billing-page">
      <EmptyState
        icon="pi-exclamation-triangle"
        title="Unable to load billing"
        description="We couldn't load your billing information. Please try again or contact support if the issue persists."
        action={
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Try Again
          </button>
        }
      />
    </div>
  )
  const sub = data?.subscription
  const usage = data?.usage
  const retention = data?.retention
  const cap = usage?.token_cap || sub?.token_cap || 0
  const used = usage?.tokens_used || 0
  const usagePct = usage?.usage_percent ?? (cap > 0 ? (used / cap) * 100 : 0)
  const pct = Math.min(100, Math.round(usagePct))
  const overSoftCap = usage?.over_soft_cap
  const softCapTokens = usage?.soft_cap_tokens || cap
  return (
    <div className="billing-page">
      <h1>Billing</h1>
      <div className="billing-info">
        <div className="billing-info-row">Plan: <b>{sub?.tier}</b> ({sub?.status})</div>
        <div className="billing-info-row">Period: {formatDateRange(usage?.period_start, usage?.period_end)}</div>
        <div className="billing-info-row">Retention: {retention?.retention_days} days (logs older than {retention?.retention_cutoff} pruned)</div>
        <div className="billing-usage">
          <div className="billing-meter">
            <div 
              className={`billing-meter-fill ${overSoftCap ? 'billing-meter-fill-warning' : 'billing-meter-fill-ok'}`}
              style={{ width: pct + '%' }} 
            />
          </div>
          <div className="billing-usage-text">{used.toLocaleString()} / {cap > 0 ? cap.toLocaleString() : '∞'} tokens</div>
          {cap > 0 && softCapTokens && (
            <div className="billing-soft-cap">
              Soft cap: {softCapTokens.toLocaleString()} tokens ({sub?.soft_grace_percent}% of plan limit)
            </div>
          )}
          {overSoftCap && (
            <div className="billing-warning">
              You have exceeded your soft usage cap. Additional usage may incur overage charges.
            </div>
          )}
        </div>
      </div>
      <div className="billing-actions">
        <button className="btn btn-primary" onClick={openPortal}>Manage subscription</button>
      </div>
    </div>
  )
}
